import i18next from 'i18next';
import { NESTED_PLATFORM_OPTIONS } from '../constants/filters';
import getGroupByOptions from '../insights/getGroupByOptions';
import {
    sortByOptions,
    orderByOptions,
} from '../social-media-posts/dropdownOptions';
import { getCompareOptions } from '../components/filters/insights/getOptions';
/**
 * Returns an array of objects
 * with 'true' as the value for each element id
 * @param {Object} Filter object
 * @return {Object} Array of objects with ids and a truthy value
 */

export const setSelected = obj =>
    obj.selected?.reduce((acc, val) => {
        acc[val] = true;
        return acc;
    }, {});

/**
 * Gets all the truthy IDs from a given
 * Selected Filters object.
 * @param {Object} Filters with selected values Object
 * @return {Array} Array of selected filters IDs
 */

export const getSelected = obj =>
    obj
        ? Object.entries(obj).reduce((acc, [id, isSelected]) => {
              if (isSelected) acc.push(id);
              return acc;
          }, [])
        : [];

/**
 * Gets all the truthy Placements ids from a given
 * Selected Rightsholders-Placements object.
 * @param {Object} Rightsholders-Placements pairs Object
 * @return {Array} Array of Placements IDs
 */
export const getSelectedPlacements = obj =>
    Object.entries(obj).reduce((acc, [id, isSelected]) => {
        if (isSelected) {
            const [rid, pids] = id.split('-');
            const ids = pids.split(',').map(pid => `${rid}-${pid}`);
            acc.push(...ids);
        }
        return acc;
    }, []);

/**
 * Gets all the truthy rightsholders ids from a given
 * Selected Rightsholders object.
 * @param {Object} Rightsholders-Leagues pairs Object
 * @return {Array} Array of RH IDs
 */
export const getSelectedNestedRightsholders = obj =>
    Object.entries(obj).reduce((acc, [id, isSelected]) => {
        const splittedIds = id.split('-');
        const newId = splittedIds[1] ? splittedIds[1] : id;
        if (isSelected && !acc.includes(newId)) acc.push(newId);
        return acc;
    }, []);

/**
 * Returns an array of strings containing the RH names
 * @param {Object} Rightsholders-Leagues pairs Object
 * @param {Object} Rightsholders Object
 * @return {Array} Array of RH names
 */
export const getRHLeagueNames = (rhsLPairs, rhs) => {
    return Object.entries(rhsLPairs).reduce((acc, [id, isSelected]) => {
        const splittedIds = id.split('-');
        const newId = splittedIds[1] ? splittedIds[1] : id;
        if (isSelected && !acc.includes(newId)) {
            acc.push(rhs.list[parseInt(newId)].name);
        }
        return acc;
    }, []);
};

/**
 * Returns an array of strings containing the names
 * of the filter selected values
 * @param {Object} Filter Object
 * @return {Array} Array of Filter Values names
 */
export const getFilterValueNames = (filter, shouldParse = true) => {
    return filter.selected
        ?.map(filterId => {
            if (filterId) {
                const parsedId = shouldParse ? parseInt(filterId) : filterId;
                if (!filter?.list) return null;
                const filterOption = filter?.list[parsedId];
                return filterOption?.name;
            }
        })
        .filter(p => p);
};

/**
 * Returns an array of strings containing the names
 * of the placement selected values
 * @param {Object} Placement Options
 * @param {Object} Placement Selection
 * @return {Array} Array of Placement names
 */
export const getPlacementNames = ({
    options: plcOptions,
    selected: plcSelected,
    hasSeveralRightsholders,
}) => {
    return plcSelected.map(plc => {
        if (!Array.isArray(plcOptions)) return null;
        if (hasSeveralRightsholders) {
            const [rid, pids] = plc.split('-');
            const placementId = pids.includes(',') ? pids : parseInt(pids);
            const parent = plcOptions.find(o => o.value === rid);
            if (!parent) return null;
            if (!parent.nested) return null;
            if (!parent.nested.find(n => n.value === placementId)) return null;
            if (!parent.nested.find(n => n.value === placementId).label)
                return null;
            return parent.nested.find(n => n.value === placementId).label;
        } else {
            if (!plcOptions.find(o => o.value === plc)) return null;
            if (!plcOptions.find(o => o.value === plc).label) return null;
            return plcOptions.find(o => o.value === plc).label;
        }
    });
};

/**
 * Returns an array of strings containing the names
 * of the selected Platforms values
 * @param {Object} Platforms Object
 * @return {Array} Array of Platforms Values names
 */
export const getPlatformsNames = platforms => {
    return platforms.selected?.map(pltId => {
        if (pltId.includes('-')) {
            return NESTED_PLATFORM_OPTIONS[pltId];
        } else {
            return platforms.list[parseInt(pltId)].name;
        }
    });
};

/**
 * Returns an array of strings containing the names
 * of the selected Group By options
 * @param {Object} Group By Object
 * @return {Array} Array of Group By  Values names
 */
export const getGroupByNames = async (
    groupBy = [],
    isComparingDates = false,
) => {
    let expandedGroupByOptions = [
        ...(await getGroupByOptions()),
        { label: 'Collection', value: 'collection' },
        { label: 'Placement Type', value: 'placementType' },
        { label: 'Broadcast Country', value: 'country' },
    ];

    if (isComparingDates) {
        expandedGroupByOptions = expandedGroupByOptions.filter(
            o => o.value !== 'date',
        );
    }

    const names = groupBy
        .map(
            value => expandedGroupByOptions.find(o => o.value === value)?.label,
        )
        .filter(p => !!p);

    if (isComparingDates) {
        names.push('Date Range');
    }
    return names;
};

export const getSortBy = ({ sort, order }) => {
    const so = sortByOptions.find(s => s.value === sort);
    const sortName = so ? so.name : '';
    const orderOptions = orderByOptions(sort) || [];
    const oo = orderOptions.find(s => s.value === order);
    const orderByName = oo ? oo.name : '';
    return `${sortName} - ${orderByName}`;
};

export const getComparedDate = ({ startDate, endDate, compareDates }) => {
    if (!compareDates?.option) return null;
    const t = i18next.getFixedT(null, 'measurement');
    const options = getCompareOptions({ startDate, endDate }, 'name', false, t);
    return options.find(o => o.value === compareDates.option)
        ? options.find(o => o.value === compareDates.option).name
        : null;
};

/**
 * Expands options that contain comma-separated suffixes
 * into individual entries with the same prefix.
 *
 * For example:
 * ['1039-108,111,116'] → ['1039-108', '1039-111', '1039-116']
 * ['2-345'] remains unchanged.
 *
 * @param {string[]} options Array of option strings
 * @return {string[]} Expanded array with separated entries
 */
export const expandPlacementOptions = options =>
    options.flatMap(opt => {
        // If no commas, return as-is
        if (!opt.includes(',')) return [opt];

        const [base, ...rest] = opt.split(',');
        const [prefix] = base.split('-');

        // Expand each suffix with the same prefix
        return [base, ...rest.map(suffix => `${prefix}-${suffix}`)];
    });
